import type { RGProduct } from "@raumagarn/formatters";

export const getSkuRank = (product: RGProduct): number => {
	const { productSku: sku } = product;
	if (!sku) return 5;

	const trimmedSku = sku.trim();

	if (product.pr_product_type_type === "yarn") {
		return 2;
	}

	// Rank 1: 3+ digits, fully numeric OR 3+ digits + dash + numbers
	if (/^\d{3,}$/.test(trimmedSku) || /^\d{3,}-\d+$/.test(trimmedSku)) {
		if (product.pr_product_type_type === "booklet") {
			// Know edge case booklet skus which should have a lower ranking
			const edgeCaseOldBookletSkus = [
				"29008",
				"29007",
				"29003",
				"29001",
				"0906159",
				"0906128",
				"090588",
				"090564",
			];

			if (edgeCaseOldBookletSkus.includes(sku)) {
				return 5;
			}
		}

		if (product.pr_product_type_type === "recipe") {
			// Know edge case booklet skus which should have a lower ranking
			const edgeCaseOldRecipeSkus = ["1208-1"];

			if (edgeCaseOldRecipeSkus.includes(sku)) {
				return 5;
			}
		}

		return 1;
	}

	// Rank 2: 3 digits + letters (with optional dash + numbers after)
	if (/^\d{3}[A-Za-z]+(-\d+)?$/.test(trimmedSku)) {
		return 3;
	}

	// Rank 3: 6+ digits only
	if (/^\d{6,}$/.test(trimmedSku)) {
		return 4;
	}

	// Rank 4: Everything else (starts with letters, etc)
	return 5;
};

// Some product lines are named with a leading catalog/pattern number (e.g. "522 Lunt
// Magdabånd"). That number becomes the first word Algolia sees, which pushes matches on
// the actual product-line name (e.g. "Islender") into a worse `attribute` ranking tier
// than products without a numeric prefix — a tier `customRanking` can't override. Only
// strips the unambiguous "digits + whitespace" case, so names where a leading number is
// part of the real text (e.g. "3-tråds flettegenser") are left untouched.
export const getSearchTitle = (name?: string) => name?.replace(/^\d+\s+/, "");

const findRecipeCategoryType = (index: string, product: RGProduct) => {
	type Category = {
		category?: string | undefined;
		name?: string[] | undefined;
		sortOrder?: number | undefined;
		uri?: string | undefined;
		attributes?:
			| {
					[key: string]: string | undefined;
			  }
			| undefined;
	};

	if (product.pr_product_type_type !== "recipe" || !product.categories) return;

	const matchingCategories = product.categories.filter((cat: Category) =>
		cat.uri?.includes(index),
	) as Category[] | undefined;

	if (!matchingCategories || matchingCategories.length === 0) return;

	return matchingCategories.map((category) => category.name?.at(-1)).filter(Boolean);
};

export const getRecipeYarnType = (product: RGProduct) => {
	return findRecipeCategoryType("/garntype/", product);
};

export const getRecipeGarmentType = (product: RGProduct) => {
	return findRecipeCategoryType("/type-plagg/", product);
};

export const getAccessoryCategory = (product: RGProduct) => {
	if (product.pr_product_type_type !== "accessory") return;

	if (!product.categories || product.categories.length === 0) return;

	const lastCategory = product.categories.at(-1);

	if (!lastCategory?.name || !Array.isArray(lastCategory.name) || lastCategory.name.length < 2)
		return;

	const category = lastCategory.name.at(1);

	if (typeof category !== "string") return;

	if (category.includes("pinner")) {
		return {
			needleCategory: category,
		};
	}

	return {
		accessoryCategory: category,
	};
};
